import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BadgeDollarSign,
  CheckCircle2,
  FileWarning,
  LifeBuoy,
  Receipt,
  TrendingUp,
} from 'lucide-react';
import { buttonVariants, EmptyState, ProgressBar } from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  formatDate,
  formatDateShort,
  formatHours,
  formatMoney,
  formatMoneySigned,
  formatPercent,
  MINUS,
} from '../../lib/format';
import { useSupportBanks, type SupportBankRow } from '../support/api';
import type { DashboardResponse } from './api';

/**
 * The three modules CARRIED OVER from the old operational dashboard when the
 * Budget & Forecast dashboard replaced it (Jesse, 2026-08-12: *"you can add the
 * 'staff utilization' and needs attention modules from the old dashboard to the
 * new one… also the receivables aging is good to see too"*).
 *
 * They are moved, not rebuilt: same components, same `/api/dashboard` fields,
 * same definitions. The expense-mix donut was dropped — the Breakdown block and
 * the P&L cover it.
 */

// ---------------------------------------------------------------------------
// Receivables ageing
// ---------------------------------------------------------------------------

type AgingKey = keyof DashboardResponse['ar']['aging'];

/** Buckets, colours and deep links match /invoices exactly — same money, same
 *  language, and each tile lands on that bucket already filtered. */
const AGING_BUCKETS: Array<{ key: AgingKey; label: string; bar: string }> = [
  { key: 'current', label: 'Current', bar: 'bg-accent' },
  { key: 'days1_30', label: '1–30 days', bar: 'bg-warning' },
  { key: 'days31_60', label: '31–60 days', bar: 'bg-orange-600' },
  { key: 'days61_90', label: '61–90 days', bar: 'bg-status-late' },
  { key: 'days90plus', label: '90+ days', bar: 'bg-primary-900' },
];

export function ReceivablesAgeing({ data }: { data: DashboardResponse }) {
  const { aging, outstanding, overdue } = data.ar;
  const total = AGING_BUCKETS.reduce((sum, b) => sum + (aging[b.key] ?? 0), 0);

  if (total === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 />}
        title="Nothing outstanding"
        description="Every issued invoice has been paid — there is no receivable to age."
      />
    );
  }

  const share = (value: number) => (total > 0 ? value / total : 0);
  const oldest = [...AGING_BUCKETS].reverse().find((b) => (aging[b.key] ?? 0) > 0);

  return (
    <div>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-grey-100"
        role="img"
        aria-label={`Receivables ageing: ${AGING_BUCKETS.filter((b) => aging[b.key] > 0)
          .map((b) => `${b.label} ${formatMoney(aging[b.key], { whole: true })}`)
          .join(', ')}`}
      >
        {AGING_BUCKETS.map((bucket) =>
          aging[bucket.key] > 0 ? (
            <div
              key={bucket.key}
              className={cn(bucket.bar, 'h-full')}
              style={{ width: `${share(aging[bucket.key]) * 100}%` }}
            />
          ) : null,
        )}
      </div>
      {/* Five buckets across a THIRD-width card (it shares its row with
          Invoices and Breakdown now, not with one other card): two up
          everywhere, three only on a genuinely wide screen. The labels are the
          ones /invoices uses and they do not get abbreviated to fit — five
          columns in a 590px card truncated "31–60 days" to "31–60 da…", which
          is a bucket boundary made unreadable to save one row, and a third of
          1440px is narrower still. */}
      <dl className="mt-3 grid grid-cols-2 gap-2 2xl:grid-cols-3">
        {AGING_BUCKETS.map((bucket) => {
          const amount = aging[bucket.key] ?? 0;
          return (
            <Link
              key={bucket.key}
              to={`/invoices?focus=aging:${bucket.key}`}
              className="group min-w-0 rounded-lg border border-grey-100 px-2.5 py-2 transition-colors hover:border-accent/40 hover:bg-grey-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              aria-label={`${bucket.label}: ${formatMoney(amount)} — open these invoices`}
            >
              <dt className="flex items-center gap-1.5 text-caption text-grey-500">
                <span className={cn('h-2 w-2 shrink-0 rounded-full', bucket.bar)} aria-hidden />
                <span className="truncate group-hover:text-accent">{bucket.label}</span>
              </dt>
              <dd
                className={cn(
                  'num truncate text-body-sm font-bold',
                  amount === 0 ? 'text-grey-300' : 'text-secondary',
                )}
              >
                {amount === 0 ? '—' : formatMoney(amount, { whole: true })}
              </dd>
              <dd className="num text-caption text-grey-500">
                {amount === 0 ? '' : formatPercent(share(amount), { decimals: 0 })}
              </dd>
            </Link>
          );
        })}
      </dl>
      {/* ⚠ THE LABELLING RULE, and it matters more here than anywhere: this card
          and the Invoices block above it show the SAME invoices under two
          opposite, both-correct readings. Aged BACKWARDS by due date here;
          rolled FORWARD into the current period there. Neither may be read as
          the other, so each says which it is in words. */}
      {/* Compressed from five lines to two. Both directional words and the
          cross-reference SURVIVE — they are the labelling rule, not padding —
          but the sentence around them does not need to restate the figures the
          tiles above it already print. */}
      <p className="mt-2.5 text-caption leading-relaxed text-grey-500">
        Aged <strong className="font-semibold">backwards</strong> by due date — how late the money
        is.{' '}
        {overdue > 0 ? (
          <>
            <span className="num font-semibold text-status-late">
              {formatMoney(overdue, { whole: true })}
            </span>{' '}
            of {formatMoney(outstanding, { whole: true })} is late
            {oldest && oldest.key !== 'current' ? `, oldest in ${oldest.label}` : ''}. The cash
            forecast rolls the same invoices <strong className="font-semibold">forward</strong> —
            when the money arrives.
          </>
        ) : (
          'Nothing is past due.'
        )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Needs attention — late invoices + support banks running low
// ---------------------------------------------------------------------------

/**
 * ⚠ **THE VARIABLE MODULE.** Jesse, after seeing it built: *"the needs
 * attention is probably going to be the most variable module so we should be
 * ready for it to push down."* Five late invoices and two support banks today;
 * twenty of each is a plausible quarter.
 *
 * So each section shows at most `VISIBLE_ROWS` and then links to the screen
 * that owns the full list. A bad month must not push the rest of the dashboard
 * off the fold — and the counts are honest: the "+N more" is computed from the
 * SERVER's total (`ar.overdueCount`), not from the length of a list the server
 * already truncated to five.
 */
const VISIBLE_ROWS = 5;

/**
 * ⚠ WHY A 46%-FULL BANK IS AT RISK — THE ONE THING THIS CARD DID NOT SAY.
 *
 * BrightPath sat here in amber reading "27.75 h left · 46% of the bank",
 * directly under Lakeshore's "9.25 h left · 13%", and read as a bug: nearly
 * half full and flagged. It is not a bug — every one of those 27.75 h expires
 * on a date the burn rate has no vote in, and both other screens say so
 * (/support: "27.75 h expires in 23 days"; /clients: "27.75 h ≈ $4,163 expire
 * in 23 days"). The app knew; this card did not tell.
 *
 * `low` has two causes with opposite remedies — a burn rate about to finish
 * the balance, versus hours about to be LOST unspent (buying more does not
 * rescue those; extending the block does). `/api/dashboard` carries neither, so
 * the cause is read from `/support/banks`, which is the same source the
 * dashboard/`/support` invariant already requires this list to agree with. When
 * it has not loaded, or is unavailable, the row renders exactly as before.
 */
function bankCause(bank: SupportBankRow | undefined): string | null {
  if (!bank || !bank.projectedExhaustion || bank.daysToExhaustion === null) return null;
  const when = `${formatDateShort(bank.projectedExhaustion)} · ${
    bank.daysToExhaustion <= 0
      ? 'today'
      : `in ${bank.daysToExhaustion} ${bank.daysToExhaustion === 1 ? 'day' : 'days'}`
  }`;
  // The calendar, not the pace — these hours go whether or not anyone burns
  // them, which is why a nearly-full bank can be the more urgent row.
  if (bank.exhaustionReason === 'expiry') {
    return `all ${formatHours(bank.balance, { unit: true })} expire ${when}`;
  }
  return `empty ${when} at ${formatHours(bank.burnPerWeek)} h/wk`;
}

export function NeedsAttention({ data }: { data: DashboardResponse }) {
  const { lateInvoices, supportAtRisk } = data;
  // Only fetched when there is something to explain.
  const { data: banks } = useSupportBanks(supportAtRisk.length > 0);
  const bankById = new Map((banks?.clients ?? []).map((b) => [b.clientId, b]));
  // The server sends the five largest late invoices; `overdueCount` is how many
  // there really are, so the overflow line cannot under-report.
  const lateHidden = Math.max(0, data.ar.overdueCount - lateInvoices.length);
  const supportShown = supportAtRisk.slice(0, VISIBLE_ROWS);
  const supportHidden = supportAtRisk.length - supportShown.length;
  const empty = lateInvoices.length === 0 && supportAtRisk.length === 0;

  if (empty) {
    return (
      <EmptyState
        icon={<CheckCircle2 />}
        title="All clear"
        description="No late invoices and no support banks running low."
      />
    );
  }

  return (
    <div className="space-y-5">
      {lateInvoices.length > 0 && (
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-caption font-semibold uppercase tracking-wider text-grey-500">
            <FileWarning className="h-3.5 w-3.5 text-status-late" aria-hidden />
            Late invoices
          </p>
          <ul className="divide-y divide-grey-100">
            {lateInvoices.map((inv) => (
              <li key={inv.id}>
                {/* Links to the thing that needs attention, not just its name:
                    this card is the screen's one call to action. */}
                <Link
                  to={`/invoices/${inv.id}`}
                  className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-grey-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-status-late-bg text-status-late">
                    <Receipt className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body-sm font-semibold text-secondary group-hover:text-accent">
                      #{inv.number} · {inv.clientName}
                    </span>
                    <span className="block text-caption text-status-late">
                      {inv.daysOverdue} {inv.daysOverdue === 1 ? 'day' : 'days'} overdue · due{' '}
                      {formatDate(inv.dueDate)}
                    </span>
                  </span>
                  <span className="num shrink-0 text-body-sm font-bold text-secondary">
                    {formatMoney(inv.dueAmount)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {lateHidden > 0 && (
            <Link
              to="/invoices?state=LATE"
              className="mt-2 inline-flex items-center gap-1 text-caption font-semibold text-accent hover:underline"
            >
              +{lateHidden} more late {lateHidden === 1 ? 'invoice' : 'invoices'}
              <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          )}
        </div>
      )}

      {supportAtRisk.length > 0 && (
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-caption font-semibold uppercase tracking-wider text-grey-500">
            <LifeBuoy className="h-3.5 w-3.5 text-status-partial" aria-hidden />
            Support hours at risk
            <Link
              to="/support"
              className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal text-accent hover:text-accent-700"
            >
              Support report
              <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          </p>
          <ul className="divide-y divide-grey-100">
            {supportShown.map((c) => {
              // Support is a BANK: the figure that matters is what is LEFT.
              // `over` = nothing left (or an allowance already exceeded).
              const over =
                c.reason === 'overage' ||
                c.reason === 'exhausted' ||
                (c.reason === 'allowance' && (c.usedHours ?? 0) >= (c.allowanceHours ?? 0));
              // The bar shows consumption, so it fills as the bank empties.
              const consumed =
                c.reason === 'allowance'
                  ? (c.allowanceHours ?? 0) > 0
                    ? (c.usedHours ?? 0) / (c.allowanceHours as number)
                    : 1
                  : 1 - (c.pctRemaining ?? 0);
              const detail =
                c.reason === 'allowance'
                  ? `${formatHours(c.usedHours ?? 0)} / ${formatHours(c.allowanceHours ?? 0, { unit: true })} allowance`
                  : c.reason === 'overage'
                    ? `${formatHours(c.balanceHours ?? 0, { unit: true })} left · ${formatHours(c.overageHours, { unit: true })} over`
                    : c.reason === 'exhausted'
                      ? `${formatHours(c.balanceHours ?? 0, { unit: true })} left in the bank`
                      : `${formatHours(c.balanceHours ?? 0, { unit: true })} left · ${formatPercent(c.pctRemaining ?? 0, { decimals: 0 })} of the bank`;
              // Why this row is amber, when the balance alone does not say it.
              const cause = c.reason === 'allowance' ? null : bankCause(bankById.get(c.clientId));
              return (
                <li key={c.clientId}>
                  <Link
                    to={`/clients/${c.clientId}`}
                    className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-grey-50"
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        over
                          ? 'bg-status-late-bg text-status-late'
                          : 'bg-status-partial-bg text-status-partial',
                      )}
                    >
                      <AlertTriangle className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body-sm font-semibold text-secondary group-hover:text-accent">
                        {c.clientName}
                      </span>
                      <span
                        className={cn(
                          'num block text-caption',
                          over ? 'text-status-late' : 'text-status-partial',
                        )}
                      >
                        {detail}
                      </span>
                      {cause && (
                        <span className="num block text-caption text-grey-500">{cause}</span>
                      )}
                    </span>
                    <ProgressBar
                      value={consumed}
                      max={1.25}
                      target={1}
                      size="sm"
                      barClassName="w-16"
                      className="shrink-0"
                      aria-label={`${c.clientName} support hours consumed ${formatPercent(consumed, { decimals: 0 })}`}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
          {supportHidden > 0 && (
            <Link
              to="/support"
              className="mt-2 inline-flex items-center gap-1 text-caption font-semibold text-accent hover:underline"
            >
              +{supportHidden} more {supportHidden === 1 ? 'client' : 'clients'} at risk
              <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff utilization — the Big Board KPI
// ---------------------------------------------------------------------------

export function StaffUtilization({
  util,
  monthLabel,
  isCurrentMonth,
}: {
  util: DashboardResponse['utilization'];
  monthLabel: string;
  isCurrentMonth: boolean;
}) {
  /*
   * ⚠ **TWO WAYS TO HAVE NO UTILIZATION, AND THEY ARE DIFFERENT FACTS.**
   * Since utilization is billable ÷ CAPACITY it is also null when the hours
   * exist but nobody has a weekly capacity set — that is a missing SETTING, not
   * a missing timesheet, and sending the reader to /time would be the wrong
   * errand. The first branch keeps the original empty state; the second says
   * what is actually absent and still prints the billable share, which is
   * computable without capacity.
   */
  if (util.totalHours === 0) {
    return (
      <EmptyState
        className="py-8"
        icon={<TrendingUp />}
        title={`No hours tracked in ${monthLabel}`}
        description="Utilization is billable hours over the capacity we pay for, so it needs a timesheet before it can say anything."
        action={
          <Link to="/time" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            Open the timesheet
          </Link>
        }
      />
    );
  }
  if (util.utilization == null) {
    return (
      <EmptyState
        className="py-8"
        icon={<TrendingUp />}
        title="No staff capacity set"
        description={`Utilization is billable ÷ capacity, and no one on staff has a weekly capacity recorded. ${
          util.billableShare != null
            ? `Billable share — the same ${formatHours(util.billableHours)} billable hours over the ${formatHours(util.totalHours)} tracked — is ${formatPercent(util.billableShare, { decimals: 0 })}.`
            : ''
        }`}
        action={
          <Link to="/team" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            Set weekly capacity
          </Link>
        }
      />
    );
  }
  return (
    /*
     * ⚠ **THIS CARD IS NOW HALF THE BOTTOM ROW**, sharing it with the
     * effective hourly rate — and that is what removed the side-by-side split
     * this block used to carry.
     *
     * The old shape was a 30rem figure column with the meter beside it,
     * because the card owned the whole 1088px content column and the width had
     * to go somewhere other than the gaps. At half a row the card body is
     * ~488px at 1440, and 30rem (480px) of that is the figure column — which
     * left the meter ~8px. The measured floor for the meter is its three-label
     * axis: below ~250px "0%", "target 75%" and "125%" run together into
     * "0%target125%".
     *
     * So the block STACKS at every width now: figure and detail on top, the
     * bar full-bleed underneath at the body's full width. That is the layout
     * this component already used below `xl` for exactly the same reason, and
     * it hands the meter 488px at 1440 — nearly double its legibility floor —
     * instead of squeezing it beside a figure that no longer has room to sit
     * next to anything.
     */
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="num text-heading-1 text-secondary">
            {formatPercent(util.utilization, { decimals: 0 })}
          </span>
          {/* ⚠ **NO MOVEMENT IS NOT GOOD NEWS.** "+0.0 pts vs last month"
              printed in success green on the second tenant, where the figure
              had not moved at all. Green is a claim; a flat month makes no
              claim, so it is grey and drops the sign. */}
          {util.previous != null &&
            (() => {
              const delta = Math.round((util.utilization! - util.previous) * 1000) / 10;
              return (
                <span
                  className={cn(
                    'num text-body-sm font-semibold',
                    delta === 0
                      ? 'text-grey-400'
                      : delta > 0
                        ? 'text-success'
                        : 'text-primary-600',
                  )}
                >
                  {delta === 0
                    ? 'unchanged vs last month'
                    : `${delta > 0 ? '+' : MINUS}${Math.abs(delta).toFixed(1)} pts vs last month`}
                </span>
              );
            })()}
        </div>
        {/* ⚠ **THE POPULATION IS IN THE SENTENCE, not only in the card's
            subtitle.** This card sits inches from the effective hourly rate,
            which counts contractors too; two figures over two different
            populations read as contradicting each other unless each says whose
            hours it is made of. "tracked by 8 staff" is that guarantee, and it
            survives a future pass that rewrites a header. */}
        <p className="num mt-1 text-body-sm text-grey-500">
          {formatHours(util.billableHours)} billable of {formatHours(util.capacityHours)} capacity
          {' · '}
          {util.peopleTracking} staff
          {util.avgHoursPerPersonPerWeek != null &&
            ` · ${formatHours(util.avgHoursPerPersonPerWeek, { unit: true })}/person/wk`}
          {isCurrentMonth ? ' · month to date' : ''}
        </p>
        {/* ⚠ **THE OTHER RATIO, PRINTED HERE UNDER ITS OWN NAME.** This card
            used to lead with THIS figure while calling it "Staff utilization",
            which is how /dashboard and /team came to print 63% and 49% for the
            same eight staff in the same month. Nothing was deleted — the
            billable share is the diagnostic that explains the utilization above
            it, and the gap between them IS the non-billable time. It carries no
            target and must never be given one. */}
        {util.billableShare != null && (
          <p className="mt-1 text-body-sm text-grey-500">
            <span className="num font-semibold text-secondary">
              {formatPercent(util.billableShare, { decimals: 0 })}
            </span>{' '}
            <strong className="font-semibold">billable share</strong> — the same billable hours
            over the <span className="num">{formatHours(util.totalHours, { unit: true })}</span>{' '}
            actually tracked.
            No target: it measures the logged day, not the paid week.
          </p>
        )}
      </div>
      <div className="min-w-0">
        <ProgressBar
          value={util.utilization}
          max={1.25}
          target={util.target}
          color="accent"
          aria-label={`Utilization, billable over capacity, ${formatPercent(util.utilization)} against a ${formatPercent(util.target, { decimals: 0 })} target`}
        />
        <p className="mt-1.5 flex justify-between text-caption text-grey-500">
          <span>0%</span>
          <span>target {formatPercent(util.target, { decimals: 0 })}</span>
          <span>125%</span>
        </p>
      </div>
      {/* ⚠ **EACH CARD NAMES ITSELF AND NAMES THE OTHER**, in prose under the
          figure — the same discipline the ageing/invoices pair carries for the
          same reason (CLAUDE.md). Two percentages-of-hours over two different
          populations, three inches apart, will be read as contradicting unless
          the difference is stated where the reader already is. Do not delete
          this sentence to save a line; its counterpart sits on the effective
          hourly rate card and neither may lose it. */}
      <p className="text-caption leading-relaxed text-grey-500">
        <strong className="font-semibold">Staff only</strong> — contractor hours are bought, not
        capacity, so they are out of this ratio. The effective hourly rate beside it counts{' '}
        <strong className="font-semibold">everyone's</strong>: what the time earned, not how much we
        could sell.{' '}
        {/* ⚠ **THE LINK CARRIES THE SCOPE, BECAUSE THE SENTENCE PROMISES A
            MATCH.** `/team` opens on **Everyone** — 12 people, 52.58% — while
            this card is staff-only, 49.39%. The tile renames itself (TEAM vs
            STAFF UTILIZATION) so neither screen states a falsehood, but a
            sentence saying "prints this same percentage" that lands on a
            different percentage is a promise the destination does not keep.
            `?type=STAFF` is the filter /team already reads off the URL, so the
            click lands on the same 49% and the other two scopes are one chip
            away — which is what the rest of this sentence offers. */}
        <Link to="/team?type=STAFF" className="font-semibold text-accent-700 hover:underline">
          /team
        </Link>{' '}
        opens on this same percentage, and can re-scope it to contractors or the whole team.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Effective hourly rate — what an hour of the firm's time is actually worth
// ---------------------------------------------------------------------------

/**
 * **THE SECOND STAFF METRIC** (Jesse, 2026-08-12: *"I like effective hourly
 * rate as a metric."*), and it sits beside utilization because they answer
 * opposite halves of one question: utilization asks whether people are BUSY,
 * this asks whether the pricing WORKS.
 *
 * `invoiced revenue ÷ every tracked hour`, year to date.
 *
 * ⚠ **THE TWO CARDS ON THIS ROW COUNT DIFFERENT PEOPLE, ON PURPOSE.**
 * Utilization is **staff only** — that is a capacity question, and a
 * contractor's hours are not our capacity. This is an **output** question, and
 * contractor hours produce revenue, so it counts **everyone who tracked time**.
 * A reader who does not know that will take the two as contradicting each
 * other, which is why the population is named in this card's subtitle AND in
 * its detail line AND in the utilization card's, three times over. Do not
 * "tidy" one of them away.
 *
 * **The meter is the diagnostic, not decoration.** The track runs $0 → the
 * average bill rate actually achieved (`revenue ÷ billable hours`); the fill
 * ends at the effective rate. The remaining gap IS the non-billable time —
 * arithmetically so, because `rate ÷ billableRate` reduces exactly to
 * `billable hours ÷ tracked hours`. One bar therefore shows the rate, the bill
 * rate and the billable share at once, which is what turns a number into an
 * explanation.
 */
export function EffectiveHourlyRate({
  metric,
}: {
  metric: DashboardResponse['effectiveRate'];
}) {
  const { rate, billableRate, billableShare, previous } = metric;

  if (rate == null) {
    return (
      <EmptyState
        className="py-8"
        icon={<BadgeDollarSign />}
        title={`No hours tracked in ${metric.windowLabel}`}
        description="The effective rate is cash collected over every tracked hour, so it needs a timesheet before it can say anything."
        action={
          <Link to="/time" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            Open the timesheet
          </Link>
        }
      />
    );
  }

  const priorRate = previous?.rate ?? null;
  const delta = priorRate == null ? null : rate - priorRate;
  // The gap the whole card exists to explain: what an hour is worth minus what
  // a BILLED hour is worth.
  const gap = billableRate == null ? null : billableRate - rate;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="num text-heading-1 text-secondary">
            {formatMoney(rate)}
            <span className="text-heading-4 font-bold text-grey-400">/h</span>
          </span>
          {delta != null && previous && (
            <span
              className={cn(
                'num text-body-sm font-semibold',
                delta >= 0 ? 'text-success' : 'text-primary-600',
              )}
            >
              {formatMoneySigned(delta)} vs {previous.windowLabel}
            </span>
          )}
        </div>
        {/* The population, spelled out in the same slot utilization spells out
            its own: whose hours these are is the fact that keeps the two cards
            from reading as one contradiction. The split is hours, not
            headcount — contractors are 20% of the denominator here and 0% of
            the one next door. */}
        <p className="num mt-1 text-body-sm text-grey-500">
          {formatHours(metric.billableHours)} billable / {formatHours(metric.totalHours)} tracked by{' '}
          {metric.peopleTracking} people · staff {formatHours(metric.staffHours, { unit: true })} +
          contractors {formatHours(metric.contractorHours, { unit: true })}
        </p>
      </div>

      {billableRate != null && billableRate > 0 && (
        <div className="min-w-0">
          <ProgressBar
            /* The fill is the effective rate as a share of the bill rate,
               which is identically the billable share of hours. `accent`
               because a rate carries no target to be judged against — the
               magnitude is the message. */
            value={rate / billableRate}
            max={1}
            color="accent"
            aria-label={`Effective rate ${formatMoney(rate)} per hour against an average bill rate of ${formatMoney(billableRate)} per hour`}
          />
          <p className="mt-1.5 flex justify-between gap-2 text-caption text-grey-500">
            <span className="num">{formatMoney(0, { whole: true })}</span>
            <span className="num text-right">
              avg bill rate {formatMoney(billableRate)}/h
            </span>
          </p>
        </div>
      )}

      {/* ⚠ **THE ALL-BILLABLE CASE IS NOT A $0.00 GAP.** The second tenant
          tracks nothing non-billable, so the general sentence would read "the
          $0.00 gap is the 0% that was not billable" — arithmetically true and
          indistinguishable from a broken figure. When there is no gap, say
          there is no gap. */}
      {gap === 0 && billableShare === 1 ? (
        /* One clause. The old version spent three lines explaining what the
           card would show if the data were different — a paragraph about the
           absence of a gap, printed where the gap would be. */
        <p className="text-caption leading-relaxed text-grey-500">
          Every tracked hour was billable, so the effective rate and the average bill rate are the
          same figure.{' '}
          <span className="text-grey-400">
            Both divide cash <span className="font-semibold text-grey-500">collected</span>, not
            invoiced.
          </span>
        </p>
      ) : gap != null && billableRate != null && billableShare != null ? (
        <p className="text-caption leading-relaxed text-grey-500">
          Every tracked hour collected{' '}
          <span className="num font-semibold text-secondary">{formatMoney(rate)}</span>; the{' '}
          {formatPercent(billableShare, { decimals: 0 })} we could bill averaged{' '}
          <span className="num font-semibold text-secondary">{formatMoney(billableRate)}</span>. The{' '}
          <span className="num font-semibold text-secondary">{formatMoney(gap)}</span> gap{' '}
          <strong className="font-semibold">is</strong> the{' '}
          {formatPercent(1 - billableShare, { decimals: 0 })} that was not billable — the pricing
          question, where utilization is the capacity one.{' '}
          {/* ⚠ THE BASIS, ON THE CARD. This rate divides money that ARRIVED, so
              hours already billed and still unpaid are in the denominator and
              not the numerator until the client pays. Saying so is what keeps
              it reconcilable with the P&L and the Revenue vs goal tile instead
              of looking like a third revenue. */}
          <span className="text-grey-400">
            Cash <span className="font-semibold text-grey-500">collected</span>, not invoiced —
            hours billed but unpaid raise the denominator now and the numerator later.
          </span>
        </p>
      ) : null}
    </div>
  );
}
